import { useCallback, useEffect, useMemo, useState } from "react";
import { uuidv7 } from "../../../core/platform/id";
import { formatPesos, parsePesos, MoneyInputError } from "../money";
import type { BooksApi, CashOut, Category, Expense, PlaceId } from "./api";
import { stockApi, type StockIngredient } from "./stock";
import { StockLines, emptyLine, parseStockLines, type StockLineDraft } from "./StockLines";

/**
 * What the café spent, and what paid for it (T014, User Story 1).
 *
 * Nothing here is ever edited. A mistake is corrected by a reversal and a
 * replacement, both shown, so the ledger reads as what happened rather than as
 * what someone last decided it should say.
 */

const PLACE_NAMES: Record<PlaceId, string> = {
  drawer: "Drawer",
  cash_held: "Cash held",
  ewallet: "E-wallet",
  capital_fund: "Capital fund",
  // Id kept from 0008; the place is the bank account (see 0009).
  treasurer: "Bank",
};

const today = () => new Date().toISOString().slice(0, 10);
const monthsAgo = (months: number) => {
  const date = new Date();
  date.setMonth(date.getMonth() - months);
  return date.toISOString().slice(0, 10);
};

export interface SpendingProps {
  api: BooksApi;
  token: string;
  version: number;
  onChanged(): void;
}

export function Spending({ api, token, version, onChanged }: SpendingProps) {
  const [from, setFrom] = useState(monthsAgo(1));
  const [to, setTo] = useState(today());
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [cashOuts, setCashOuts] = useState<CashOut[]>([]);
  const [total, setTotal] = useState(0);
  const [form, setForm] = useState<{ cashOut?: CashOut; correcting?: Expense } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const categoryName = useMemo(
    () => new Map(categories.map((category) => [category.id, category.name])),
    [categories],
  );

  const load = useCallback(async () => {
    try {
      const [list, totals, waiting, cats] = await Promise.all([
        api.expenses(from, to),
        api.totals(from, to),
        api.cashOuts(),
        api.categories(),
      ]);
      setExpenses(list.expenses);
      setTotal(totals.total);
      setCashOuts(waiting.cashOuts);
      setCategories(cats.categories);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load spending");
    }
  }, [api, from, to]);

  useEffect(() => {
    void load();
  }, [load, version]);

  const done = (message: string) => {
    setForm(null);
    setNotice(message);
    onChanged();
    void load();
  };

  if (form) {
    return (
      <PurchaseForm
        api={api}
        token={token}
        categories={categories}
        cashOut={form.cashOut ?? null}
        correcting={form.correcting ?? null}
        onDone={done}
        onCancel={() => setForm(null)}
      />
    );
  }

  return (
    <section className="books__panel">
      {error && (
        <p className="books__error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="books__notice" role="status">
          {notice}
        </p>
      )}

      {cashOuts.length > 0 && (
        <>
          <h3 className="books__subhead">Waiting for a category</h3>
          <ul className="books__list">
            {cashOuts.map((cashOut) => (
              <li key={cashOut.id} className="books__row">
                <span className="books__row-name">{cashOut.reason}</span>
                <span className="books__figure">₱{formatPesos(cashOut.amount)}</span>
                <button type="button" className="books__action" onClick={() => setForm({ cashOut })}>
                  Categorise
                </button>
                <span className="books__row-meta">
                  Taken from the drawer by {cashOut.staffName ?? "staff"} ·{" "}
                  {new Date(cashOut.createdAt).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="books__pickers">
        <label className="books__field">
          <span>From</span>
          <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label className="books__field">
          <span>To</span>
          <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
        </label>
      </div>

      <p className="books__total">
        Spent in this period <strong>₱{formatPesos(total)}</strong>
      </p>

      {expenses.length === 0 ? (
        <p className="books__hint">Nothing recorded in these dates.</p>
      ) : (
        <ul className="books__list">
          {expenses.map((expense) => (
            <li key={expense.id} className="books__row">
              <span className="books__row-name">
                {expense.description}
                {expense.isEstimate && <span className="books__chip">estimate</span>}
                {expense.correctsId && <span className="books__chip">correction</span>}
                {expense.cashMovementId && <span className="books__chip">from the till</span>}
              </span>
              <span className={`books__figure${expense.amount < 0 ? " is-back" : ""}`}>
                ₱{formatPesos(expense.amount)}
              </span>
              {!expense.correctsId && (
                <button type="button" className="books__action" onClick={() => setForm({ correcting: expense })}>
                  Correct
                </button>
              )}
              <span className="books__row-meta">
                {expense.dateText ?? expense.spentOn} · {categoryName.get(expense.categoryId) ?? expense.categoryId}
                {expense.supplier ? ` · ${expense.supplier}` : ""} ·{" "}
                {expense.parts
                  .map((part) => (part.place ? PLACE_NAMES[part.place] : "a partner's own pocket"))
                  .join(" + ")}
                {expense.reason ? ` · ${expense.reason}` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="books__buttons">
        <button type="button" className="books__action is-primary" onClick={() => setForm({})}>
          + Purchase
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------

interface PurchaseFormProps {
  api: BooksApi;
  token: string;
  categories: Category[];
  cashOut: CashOut | null;
  correcting: Expense | null;
  onDone(message: string): void;
  onCancel(): void;
}

/**
 * One form for three jobs: a new purchase, giving a till cash-out its category,
 * and correcting an expense. They differ only in what is fixed — a cash-out's
 * amount and money are already decided, and a correction keeps the original's
 * date.
 *
 * For a goods category it also offers what came in, so an ingredient delivery
 * is one entry rather than two (T015, FR-129).
 */
function PurchaseForm({ api, token, categories, cashOut, correcting, onDone, onCancel }: PurchaseFormProps) {
  const [opId] = useState(() => uuidv7());
  const [supplier, setSupplier] = useState(correcting?.supplier ?? "");
  const [description, setDescription] = useState(correcting?.description ?? cashOut?.reason ?? "");
  const [categoryId, setCategoryId] = useState(correcting?.categoryId ?? categories[0]?.id ?? "");
  const [amount, setAmount] = useState(
    correcting ? formatPesos(correcting.amount) : cashOut ? formatPesos(cashOut.amount) : "",
  );
  const [place, setPlace] = useState<PlaceId>(
    cashOut ? "drawer" : ((correcting?.parts[0]?.place as PlaceId) ?? "ewallet"),
  );
  const [spentOn, setSpentOn] = useState(correcting?.spentOn ?? today());
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Null until we know stock is available to this person, and for this kind of purchase. */
  const [stock, setStock] = useState<StockIngredient[] | null>(null);
  const [lines, setLines] = useState<StockLineDraft[]>(() => [emptyLine()]);

  const isGoods = categories.find((category) => category.id === categoryId)?.kind === "goods";
  /*
   * A correction is about the money, not the stock: the ingredients really did
   * arrive, whatever the receipt said. Wrongly received stock is fixed in
   * inventory's own count, where it can be explained.
   */
  const offersStock = isGoods && correcting === null;

  useEffect(() => {
    if (!offersStock) {
      setStock(null);
      return;
    }

    let live = true;
    void stockApi(token)
      .ingredients()
      .then((body) => {
        // Retired ingredients are still bought out of habit; don't offer them.
        if (live) setStock(body.ingredients.filter((ingredient) => ingredient.active !== false));
      })
      .catch(() => {
        // Switched off, not permitted, or offline — record the spending alone.
        if (live) setStock(null);
      });

    return () => {
      live = false;
    };
  }, [offersStock, token]);

  const title = cashOut ? "Categorise a cash-out" : correcting ? "Correct a purchase" : "New purchase";

  const submit = async (reverseOnly = false) => {
    setError(null);

    if (correcting && reason.trim() === "") {
      setError("Say what was wrong.");
      return;
    }

    let centavos = 0;
    if (!reverseOnly) {
      try {
        centavos = parsePesos(amount);
      } catch (caught) {
        setError(caught instanceof MoneyInputError ? caught.message : "That is not an amount.");
        return;
      }
      if (centavos === 0) {
        setError("Enter how much it was.");
        return;
      }
      if (description.trim() === "") {
        setError("Say what it was for.");
        return;
      }
    }

    let received: ReturnType<typeof parseStockLines> = [];
    if (!reverseOnly && stock !== null) {
      try {
        received = parseStockLines(lines, stock);
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "Check the stock lines.");
        return;
      }
    }

    const body = {
      spentOn,
      supplier: supplier.trim() || null,
      description: description.trim(),
      categoryId,
      amount: centavos,
      parts: [{ place, amount: centavos }],
      ...(cashOut ? { cashMovementId: cashOut.id } : {}),
      ...(received.length > 0 ? { inventoryOpId: opId } : {}),
    };

    setBusy(true);
    try {
      /*
       * Stock first, then the money. Both are keyed on this form's single
       * opId, so a failure between them is repaired by pressing Save again:
       * the movements already written are recognised as duplicates and the
       * expense lands once.
       */
      if (received.length > 0) {
        const inventory = stockApi(token);
        for (const line of received) {
          await inventory.receiving({
            opId,
            ingredientId: line.ingredientId,
            quantity: line.quantity,
            unitCost: line.unitCost,
          });
        }
      }

      if (correcting) {
        await api.correctExpense(correcting.id, {
          opId,
          reason: reason.trim(),
          replacement: reverseOnly ? null : body,
        });
        onDone(reverseOnly ? "Reversed. The original stays on the record." : "Corrected.");
      } else {
        await api.createExpense({ opId, ...body });
        const money = `₱${formatPesos(centavos)}`;
        const stockSaid =
          received.length === 0
            ? ""
            : ` ${received.length} ingredient${received.length === 1 ? "" : "s"} added to stock.`;
        onDone(
          cashOut
            ? `${money} from the drawer recorded as ${categories.find((c) => c.id === categoryId)?.name ?? "spending"}.${stockSaid}`
            : `${money} recorded.${stockSaid}`,
        );
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not record it");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="books__panel">
      <h3 className="books__subhead">{title}</h3>
      {cashOut && (
        <p className="books__hint">
          ₱{formatPesos(cashOut.amount)} taken from the drawer by {cashOut.staffName ?? "staff"}. The money
          has already left the drawer — this only says what it was for.
        </p>
      )}
      {correcting && (
        <p className="books__hint">
          The original stays on the record. This writes a reversal and, unless you reverse it entirely,
          a replacement.
        </p>
      )}

      <label className="books__field">
        <span>What it was</span>
        <input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={300} autoFocus />
      </label>

      <div className="books__pickers">
        <label className="books__field">
          <span>Category</span>
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </label>

        <label className="books__field">
          <span>How much (pesos)</span>
          <input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            disabled={cashOut !== null}
          />
        </label>
      </div>

      <div className="books__pickers">
        <label className="books__field">
          <span>Paid from</span>
          <select
            value={place}
            onChange={(e) => setPlace(e.target.value as PlaceId)}
            disabled={cashOut !== null}
          >
            {(["ewallet", "cash_held", "capital_fund"] as PlaceId[]).map((id) => (
              <option key={id} value={id}>
                {PLACE_NAMES[id]}
              </option>
            ))}
            {cashOut && <option value="drawer">Drawer</option>}
          </select>
        </label>

        <label className="books__field">
          <span>Supplier</span>
          <input value={supplier} onChange={(e) => setSupplier(e.target.value)} maxLength={200} />
        </label>
      </div>

      {!cashOut && (
        <label className="books__field">
          <span>When</span>
          <input type="date" value={spentOn} onChange={(e) => setSpentOn(e.target.value)} />
        </label>
      )}

      {stock !== null && <StockLines stock={stock} lines={lines} onChange={setLines} />}

      {correcting && (
        <label className="books__field">
          <span>What was wrong</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} />
        </label>
      )}

      {error && (
        <p className="books__error" role="alert">
          {error}
        </p>
      )}

      <div className="books__buttons">
        <button type="button" className="books__action" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        {correcting && (
          <button type="button" className="books__action is-danger" onClick={() => void submit(true)} disabled={busy}>
            Reverse it entirely
          </button>
        )}
        <button type="button" className="books__action is-primary" onClick={() => void submit()} disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </section>
  );
}
